import React, { useState, useEffect } from 'react';
import { X, UserPlus, Mail, User as UserIcon, Search, Plus } from 'lucide-react';
import { usersAPI } from '../../services/api';
import { User } from '../../types';
import toast from 'react-hot-toast';

interface AddCoauthorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCoauthor: (coauthorData: { name: string; email: string; userId?: number }) => void;
  publicationTitle: string;
  isLoading?: boolean;
}

const AddCoauthorModal: React.FC<AddCoauthorModalProps> = ({
  isOpen,
  onClose,
  onAddCoauthor,
  publicationTitle,
  isLoading = false
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const allUsers = await usersAPI.getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const firstName = user.first_name || user.firstName || '';
    const lastName = user.last_name || user.lastName || '';
    const email = user.email || '';
    
    return firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setIsManualEntry(false);
    setManualName('');
    setManualEmail('');
  };

  const handleManualEntry = () => {
    setIsManualEntry(true);
    setSelectedUser(null);
    setSearchTerm('');
  };

  const handleSubmit = () => {
    if (selectedUser) {
      // Add existing user
      const firstName = selectedUser.first_name || selectedUser.firstName || '';
      const lastName = selectedUser.last_name || selectedUser.lastName || '';
      onAddCoauthor({
        name: `${firstName} ${lastName}`.trim(),
        email: selectedUser.email,
        userId: selectedUser.id
      });
    } else if (isManualEntry && manualName.trim() && manualEmail.trim()) {
      // Add new user manually
      onAddCoauthor({
        name: manualName.trim(),
        email: manualEmail.trim()
      });
    } else {
      toast.error('Please select a user or enter name and email');
      return;
    }
    
    // Reset form
    setSelectedUser(null);
    setIsManualEntry(false);
    setManualName('');
    setManualEmail('');
    setSearchTerm('');
    onClose();
  };

  const handleClose = () => {
    setSelectedUser(null);
    setIsManualEntry(false);
    setManualName('');
    setManualEmail('');
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserPlus className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add Co-author</h2>
              <p className="text-sm text-gray-600">Add a co-author to this publication</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Publication Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Publication:</h3>
            <p className="text-gray-700">{publicationTitle}</p>
          </div>

          {/* Search Existing Users */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Search Existing Users</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* User List */}
            {isLoadingUsers ? (
              <div className="mt-3 text-center text-gray-500">Loading users...</div>
            ) : (
              <div className="mt-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {searchTerm ? 'No users found' : 'Start typing to search users'}
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      className={`w-full p-3 text-left border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
                        selectedUser?.id === user.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gray-100 rounded-full">
                          <UserIcon className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.first_name || user.firstName} {user.last_name || user.lastName}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {user.email}
                          </div>
                          {user.institution && (
                            <div className="text-xs text-gray-500">{user.institution}</div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Manual Entry Option */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Add New Person</h3>
              <button
                onClick={handleManualEntry}
                className="flex items-center space-x-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add manually</span>
              </button>
            </div>

            {isManualEntry && (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      placeholder="Enter full name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Selected User Display */}
          {selectedUser && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Selected Co-author:</h4>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <UserIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-blue-900">
                    {selectedUser.first_name || selectedUser.firstName} {selectedUser.last_name || selectedUser.lastName}
                  </div>
                  <div className="text-sm text-blue-700 flex items-center">
                    <Mail className="h-3 w-3 mr-1" />
                    {selectedUser.email}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={(!selectedUser && (!isManualEntry || !manualName.trim() || !manualEmail.trim())) || isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Adding...</span>
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" />
                <span>Add Co-author</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCoauthorModal;
